import React, { useState } from 'react';
import { X, Sparkles, Copy, Save, Loader2, ArrowRight, Zap, Brain, Target, TrendingUp } from 'lucide-react';
import { openAIService } from '../../services/openai';
import { Prompt } from '../../types';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useScrollLock } from '../../hooks/useScrollLock';

interface PromptEnhanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalPrompt: Prompt;
  onSaveEnhanced: (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void;
  onUpdateOriginal: (id: string, updates: Partial<Prompt>) => void;
}

export const PromptEnhanceModal: React.FC<PromptEnhanceModalProps> = ({
  isOpen,
  onClose,
  originalPrompt,
  onSaveEnhanced,
  onUpdateOriginal,
}) => {
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const modalRef = useClickOutside(() => {
    if (isOpen && !isEnhancing) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const handleEnhance = async () => {
    setIsEnhancing(true);
    setError(null);
    setEnhancedPrompt('');

    try {
      const enhanced = await openAIService.enhancePrompt(originalPrompt.body);
      setEnhancedPrompt(enhanced);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to enhance prompt';
      console.error('🔴 Prompt enhancement error:', err);
      
      // Show user-friendly error with setup guidance
      if (errorMessage.includes('API key not configured')) {
        setError('OpenAI API key not configured. Please set up VITE_OPENAI_API_KEY in your Netlify environment variables and redeploy the site.');
      } else if (errorMessage.includes('API key')) {
        setError('Invalid OpenAI API key. Please check that your API key is correct and starts with "sk-".');
      } else if (errorMessage.includes('rate limit')) {
        setError('OpenAI API rate limit exceeded. Please try again in a moment.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(enhancedPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleSaveAsNew = () => {
    onSaveEnhanced({
      title: `${originalPrompt.title} (Enhanced)`,
      body: enhancedPrompt,
      aiPlatform: originalPrompt.aiPlatform,
      isFavorite: false,
      isTemplate: originalPrompt.isTemplate,
      domain: originalPrompt.domain,
      output: originalPrompt.output,
      platforms: originalPrompt.platforms,
      webPlatforms: originalPrompt.webPlatforms,
    });
    onClose();
  };

  const handleReplaceOriginal = () => {
    if (window.confirm('Are you sure you want to replace the original prompt with the enhanced version? This action cannot be undone.')) {
      onUpdateOriginal(originalPrompt.id, {
        body: enhancedPrompt,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center">
            <Sparkles className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-3" />
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text">Enhance Prompt</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Improve "{originalPrompt.title}" with AI optimization</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Simple Description */}
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">
              Refine your prompt's clarity, structure, and impact.
            </p>
          </div>

          {/* Before and After Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Original Prompt */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Original Prompt
              </h3>
              <div className="bg-gray-50 dark:bg-dark-card rounded-lg p-4 border border-gray-200 dark:border-dark-border min-h-[180px]">
                <p className="text-gray-900 dark:text-dark-text text-sm leading-relaxed whitespace-pre-wrap">
                  {originalPrompt.body}
                </p>
              </div>
            </div>

            {/* Enhanced Prompt */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enhanced Prompt
              </h3>
              
              <div className="bg-gray-50 dark:bg-dark-card rounded-lg p-4 border border-gray-200 dark:border-dark-border min-h-[180px] flex items-center justify-center">
                {isEnhancing ? (
                  <div className="text-center space-y-4">
                    <div className="relative mb-3">
                      <Brain className="h-8 w-8 text-gray-400 animate-pulse mx-auto" />
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">
                        Enhancing...
                      </p>
                      <p className="text-gray-500 dark:text-gray-500 text-sm">
                        Refining structure and clarity
                      </p>
                    </div>
                    <div className="space-y-2 mt-4">
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-4/5 mx-auto"></div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4 mx-auto"></div>
                    </div>
                  </div>
                ) : enhancedPrompt ? (
                  <div className="w-full">
                    <p className="text-gray-900 dark:text-dark-text text-sm leading-relaxed whitespace-pre-wrap">
                      {enhancedPrompt}
                    </p>
                  </div>
                ) : (
                  <div className="text-center space-y-6">
                    <div className="mb-4">
                      <Sparkles className="h-6 w-6 text-gray-400 mx-auto" />
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400 font-medium">
                        ✨ Your enhanced prompt will appear here
                      </p>
                      <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                        AI-refined for clarity, structure, and impact.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Primary CTA - Full Width, High Contrast */}
          {!enhancedPrompt && !isEnhancing && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => handleEnhance()}
                className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100
                  px-8 py-3 rounded-lg font-medium transition-all duration-200 
                  shadow-sm hover:shadow-md transform hover:scale-[1.02] active:scale-[0.98]
                  flex items-center justify-center min-w-[200px]"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                <span>Enhance Now</span>
              </button>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Action Buttons - Only when enhanced */}
          {enhancedPrompt && (
            <div className="flex flex-col sm:flex-row items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3 
              pt-4 border-t border-gray-200 dark:border-dark-border">
              
              {/* Secondary Actions */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCopy}
                  className={`flex items-center px-3 py-2 text-sm rounded-md transition-all duration-200 ${
                    copied
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copied ? 'Copied!' : 'Copy Enhanced'}
                </button>
                
                <button
                  onClick={handleSaveAsNew}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300 
                    hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-all duration-200 
                    border border-gray-200 dark:border-gray-600"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save as New
                </button>
              </div>
              
              {/* Primary Action */}
              <button
                onClick={handleReplaceOriginal}
                className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100
                  px-6 py-2 rounded-md font-medium transition-all duration-200 
                  transform hover:scale-[1.02] active:scale-[0.98] 
                  flex items-center"
              >
                Replace Original
              </button>
            </div>
          )}

          {/* Try Again Option */}
          {enhancedPrompt && (
            <div className="text-center pt-3">
              <button
                onClick={() => {
                  setEnhancedPrompt('');
                  setError(null);
                }}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 
                  transition-colors duration-200"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};